import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { addRecruitAPI } from '../../../../actions/college-corner.actions';
import { showErrorToast, showSuccessToast } from '../../../../actions/notification-actions';
import Img from '../../../../components/Img';
import PopOverMenu from '../../../../components/PopOverMenu';
import useClickOutsideRef from '../../../../components/hooks/useClickOutsideRef';
import useCPPTracking from '../../../../components/hooks/useCPPTracking';
import {
    CppCoachActions,
    CppCoachSurfaces,
    CppContactMethods,
} from '../../../../components/Analytics/constants';
import { canCoachAddPlayer } from './cppConstants';
import styles from './MoveToRecruitsCell.scss';

const HTTP_CONFLICT = 409;

export const ADD_LABEL = 'Add to: "Prospects I\'m Interested In" on your college page.';
export const ADDED_LABEL = 'Added to "Prospects I\'m Interested In" on your college page';

const ADDED_TOAST_TITLE = 'Added to "Prospects I\'m Interested In"';
const ADD_SUCCESS_MESSAGE = 'Player added to your "Prospects I\'m Interested In" list on your college page.';
const ADD_ERROR_MESSAGE = 'We couldn\'t add this player. Please try again.';
const GENDER_MISMATCH_MESSAGE = 'You can only add players that match your program\'s gender.';
const UNCLAIMED_MESSAGE = 'This player hasn\'t claimed their UTR profile yet, so they can\'t be contacted or added.';

const CONTACT_ICON = '/img/v2/icons/icon-chat-blue.svg';

const ContactPopover = ({ phone, email, onContactInitiated, 'data-testid': dataTestId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);
    useClickOutsideRef(wrapperRef, () => setIsOpen(false));

    return (
        <span className={styles.contactPopover} ref={wrapperRef}>
            <button
                type="button"
                className={styles.contactAnchor}
                onClick={() => setIsOpen((open) => !open)}
                aria-label="Contact"
                data-testid={dataTestId && `${dataTestId}.anchor`}
            >
                <Img src={CONTACT_ICON} alt="Contact" className={styles.contactIcon} />
            </button>
            {isOpen && (
                <div className={styles.contactCard} data-testid={dataTestId && `${dataTestId}.card`}>
                    {phone && (
                        <a className={styles.contactRow} href={`tel:${phone}`} onClick={() => onContactInitiated?.(CppContactMethods.PHONE)} data-testid={dataTestId && `${dataTestId}.call`}>
                            <span className={styles.contactLabel} data-testid={dataTestId && `${dataTestId}.call.label`}>{'Call: '}</span>
                            <span className={styles.contactValue}>{phone}</span>
                        </a>
                    )}
                    {phone && (
                        <a className={styles.contactRow} href={`sms:${phone}`} onClick={() => onContactInitiated?.(CppContactMethods.TEXT)} data-testid={dataTestId && `${dataTestId}.text`}>
                            <span className={styles.contactLabel} data-testid={dataTestId && `${dataTestId}.text.label`}>{'Text: '}</span>
                            <span className={styles.contactValue}>{phone}</span>
                        </a>
                    )}
                    {email && (
                        <a className={styles.contactRow} href={`mailto:${email}`} onClick={() => onContactInitiated?.(CppContactMethods.EMAIL)} data-testid={dataTestId && `${dataTestId}.email`}>
                            <span className={styles.contactLabel} data-testid={dataTestId && `${dataTestId}.email.label`}>{'Email: '}</span>
                            <span className={styles.contactValue}>{email}</span>
                        </a>
                    )}
                </div>
            )}
        </span>
    );
};

ContactPopover.propTypes = {
    phone: PropTypes.string,
    email: PropTypes.string,
    onContactInitiated: PropTypes.func,
    'data-testid': PropTypes.string,
};

ContactPopover.defaultProps = {
    phone: '',
    email: '',
    onContactInitiated: null,
    'data-testid': null,
};

const MoveToRecruitsCell = ({ item, collegeCoachTeamGenders, 'data-testid': dataTestId }) => {
    const dispatch = useDispatch();
    const { trackCoachEngagement } = useCPPTracking();

    const source = item?.source || {};
    const cpp = item?.cpp || null;
    const targetMemberId = source.memberId || null;
    const isClaimed = !!cpp && !!targetMemberId;
    const playerGender = source.gender || null;
    const email = cpp?.email || '';
    const phone = cpp?.phone || '';

    const [added, setAdded] = useState(!!cpp?.alreadyRecruit);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setAdded(!!cpp?.alreadyRecruit);
    }, [targetMemberId, cpp?.alreadyRecruit]);

    const isGenderMismatch = !canCoachAddPlayer(collegeCoachTeamGenders, playerGender);

    const handleAdd = async () => {
        if (!isClaimed || isSubmitting || added) {
            return;
        }
        setIsSubmitting(true);
        setAdded(true);
        try {
            const res = await dispatch(addRecruitAPI(targetMemberId));
            trackCoachEngagement(
                CppCoachActions.CPP_COACH_PROSPECT_LIKED,
                CppCoachSurfaces.COLLEGE_CORNER,
                { playerId: targetMemberId },
            );
            dispatch(showSuccessToast({
                title: ADDED_TOAST_TITLE,
                message: res?.data?.message || ADD_SUCCESS_MESSAGE,
            }));
        } catch (err) {
            // 409 means the player is already a recruit — keep the added state, no error.
            if (err?.response?.status === HTTP_CONFLICT) {
                return;
            }
            setAdded(false);
            dispatch(showErrorToast({ message: err?.response?.data?.message || ADD_ERROR_MESSAGE }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const onActionSelected = () => {
        if (added) {
            return;
        }
        if (isGenderMismatch) {
            dispatch(showErrorToast({ message: GENDER_MISMATCH_MESSAGE }));
            return;
        }
        handleAdd();
    };

    let actionLabel = ADD_LABEL;
    if (added) {
        actionLabel = <span className={styles.disabledItem}>{ADDED_LABEL}</span>;
    } else if (isGenderMismatch) {
        actionLabel = <span className={styles.disabledItem} title={GENDER_MISMATCH_MESSAGE}>{ADD_LABEL}</span>;
    }

    const hasContact = !!(email || phone);

    return (
        <span className={styles.cell} data-testid={dataTestId || undefined}>
            {isClaimed && hasContact ? (
                <ContactPopover
                    phone={phone}
                    email={email}
                    onContactInitiated={(contactMethod) => {
                        trackCoachEngagement(
                            CppCoachActions.CPP_COACH_CONTACT_INITIATED,
                            CppCoachSurfaces.COLLEGE_CORNER,
                            { contact_method: contactMethod, playerId: targetMemberId },
                        );
                    }}
                    data-testid={dataTestId && `${dataTestId}.contact`}
                />
            ) : (
                <span
                    className={`${styles.contactAnchor} ${styles.disabled}`}
                    title={!isClaimed ? UNCLAIMED_MESSAGE : undefined}
                    data-testid={dataTestId && `${dataTestId}.contact-disabled`}
                >
                    <Img src={CONTACT_ICON} alt="Contact unavailable" className={styles.contactIcon} />
                </span>
            )}

            {isClaimed ? (
                <PopOverMenu
                    menuItems={[{ label: actionLabel, value: 'move' }]}
                    onSelected={onActionSelected}
                    popOverMenuClass={styles.actionsMenu}
                    data-testid={dataTestId && `${dataTestId}.actions`}
                />
            ) : (
                <span
                    className={`far fa-ellipsis-v ${styles.kebab} ${styles.disabled}`}
                    title={UNCLAIMED_MESSAGE}
                    data-testid={dataTestId && `${dataTestId}.actions-disabled`}
                />
            )}
        </span>
    );
};

MoveToRecruitsCell.propTypes = {
    item: PropTypes.instanceOf(Object).isRequired,
    collegeCoachTeamGenders: PropTypes.arrayOf(PropTypes.string),
    'data-testid': PropTypes.string,
};

MoveToRecruitsCell.defaultProps = {
    collegeCoachTeamGenders: [],
    'data-testid': null,
};

export default MoveToRecruitsCell;
